import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import { transformAmount } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/FormModal/helpers/paywallCustomizationSerializer";
import {
  isInstallmentsPrice,
  isOneTimePrice,
} from "@circle-react/helpers/paywallPriceHelpers";
import type { CouponApplication } from "@circle-react/types";
import type { ReducedPrice } from "@circle-react/types/PaywallCustomization";
import { defaultFormatPaywallCurrency } from "../constants";
import type { PriceScenariosProps } from "../types";
import { Title } from "./Title";

const getPriceAttributes = (
  price: number,
  couponApplication?: CouponApplication,
  reducedPrice?: ReducedPrice,
) => {
  let totalPrice = price;
  let totalPriceDiscounted = -1;

  if (couponApplication) {
    totalPrice = price;
    totalPriceDiscounted = Math.floor(
      price - (couponApplication?.discount_amount || 0),
    );
  } else if (reducedPrice && transformAmount(reducedPrice.amount) > price) {
    totalPrice = transformAmount(reducedPrice.amount);
    totalPriceDiscounted = price;
  }

  return {
    totalPrice,
    totalPriceDiscounted,
  };
};

const getTitleAndOldTitle = (
  totalPrice: number,
  paywall: PriceScenariosProps["paywall"],
  totalPriceDiscounted: number,
) => {
  let oldTitle;
  let title;

  if (totalPriceDiscounted >= 0) {
    oldTitle = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPriceDiscounted,
      defaultFormatPaywallCurrency,
    );
  } else {
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
  }

  return {
    title,
    oldTitle,
  };
};

const getSubscriptionPrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps) => {
  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    price.amount,
    couponApplication,
    reducedPrice,
  );

  return {
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getInstallmentPrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps) => {
  if (!isInstallmentsPrice(price)) {
    return null;
  }

  const totalAmount = Math.floor(
    price.amount - (price.upfront_payment_amount || 0),
  );

  // We only need to render the first installment, so we need to pass
  // only the installment amount, and not the total.
  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    totalAmount / (price.installments_count || 1),
    couponApplication,
    reducedPrice,
  );

  return {
    prefix: `${price.installments_count}x`,
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getOneTimePrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps) => {
  if (!isOneTimePrice(price)) {
    return null;
  }

  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    price.amount,
    couponApplication,
    reducedPrice,
  );

  return {
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
  };
};

export const Value = (props: PriceScenariosProps) => (
  <Title
    {...(getOneTimePrice(props) ||
      getInstallmentPrice(props) ||
      getSubscriptionPrice(props))}
  />
);
